import React, { useState, useEffect } from 'react';
import ManagerSidebar from '../components/Sidebar';
import { useSidebar } from '../context/SidebarContext';
import { useAuth } from '../context/AuthContext';
import { db } from '../config/firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';

function ManagerOverview() {
  const { isMobile, isOpen, toggleMobileSidebar } = useSidebar();
  const { currentUser } = useAuth();

  const [stats, setStats] = useState({
    totalQueries: 0,
    activeQueries: 0,
    completedQueries: 0
  });

  const [recentQueries, setRecentQueries] = useState([]);

  useEffect(() => {
    if (!currentUser) return;

    const q = query(collection(db, 'queries'), where('createdBy', '==', currentUser.uid));
    const unsub = onSnapshot(q, (snapshot) => {
      const queries = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      const active = queries.filter(q => {
        const s = (q.status || '').toLowerCase();
        return !['paid', 'completed', 'closed', 'rejected', 'cancelled'].includes(s);
      }).length;
      const completed = queries.filter(q => {
        const s = (q.status || '').toLowerCase();
        return ['paid', 'completed', 'closed'].includes(s);
      }).length;

      queries.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return dateB - dateA;
      });

      setStats({ totalQueries: queries.length, activeQueries: active, completedQueries: completed });
      setRecentQueries(queries.slice(0, 5));
    }, (error) => {
      console.error("Error fetching queries:", error);
    });

    return () => unsub();
  }, [currentUser]);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = (now - date) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} mins ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    return `${Math.floor(diff / 86400)} days ago`;
  };

  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    if (s.includes('pending')) return 'text-yellow-600';
    if (s.includes('completed') || s.includes('paid')) return 'text-green-600';
    if (s.includes('reject')) return 'text-red-600';
    return 'text-blue-600';
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <ManagerSidebar />
      <div
        className="flex-1 overflow-y-auto transition-all duration-300"
        style={{ marginLeft: isMobile ? 0 : (isOpen ? '16rem' : '5rem') }}
      >
        <div className="p-6 min-h-screen">
          <div className="mb-6">
            <div className="flex items-center">
              {isMobile && (
                <button onClick={toggleMobileSidebar} className="mr-4 p-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:text-brand-golden">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              )}
              <div>
                <h1 className="text-3xl font-bold text-brand-dark">Manager Dashboard</h1>
                <p className="text-gray-600 mt-1">Overview of your service requests.</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-brand-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">My Queries</p>
                  <h3 className="text-2xl font-bold text-brand-dark mt-1">{stats.totalQueries}</h3>
                </div>
                <div className="bg-brand-golden/10 p-3 rounded-lg">
                  <svg className="w-6 h-6 text-brand-golden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-brand-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active</p>
                  <h3 className="text-2xl font-bold text-blue-600 mt-1">{stats.activeQueries}</h3>
                </div>
                <div className="bg-blue-100 p-3 rounded-lg">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-brand-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Completed</p>
                  <h3 className="text-2xl font-bold text-green-600 mt-1">{stats.completedQueries}</h3>
                </div>
                <div className="bg-green-100 p-3 rounded-lg">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-brand-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-xl font-semibold text-brand-dark mb-4">Recent Queries</h2>
            <div className="space-y-4">
              {recentQueries.length > 0 ? (
                recentQueries.map((query) => (
                  <div key={query.id} className="flex items-center space-x-4 p-4 hover:bg-gray-50 rounded-lg transition-colors">
                    <div className="bg-blue-100 p-3 rounded-lg">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-brand-dark">{query.title || query.serviceType || 'Service Request'}</p>
                      <p className="text-sm text-gray-600">{query.location || query.address}</p>
                    </div>
                    <div className="text-right">
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${getStatusColor(query.status)} bg-opacity-10`}>
                        {query.status}
                      </span>
                      <p className="text-xs text-gray-400 mt-1">{formatTime(query.createdAt)}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">No queries found. Create your first service request!</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ManagerOverview;
